/**
 * Mock data for the Acquisition root page.
 *
 * Values ported from the source dashboard (OWS-Homepage acquisitionData.ts, read
 * from the Figma frames). Throwaway data — when wired to real endpoints, replace
 * this with a selector/saga producing the same `AcquisitionData` shape, keyed on
 * the active brand. The hero gauge's score is NOT a hard-coded figure: it is
 * derived from this brand's campaign queue + budget via `computeAcquisitionScore`
 * (see `pages/acquisition/acquisition-score`), so the future selector can keep
 * feeding the same formula real numbers.
 *
 * Per-brand mock: since there is no brand-scoped API yet, a handful of brand
 * "profiles" ({@link ACQ_BRAND_VARIANTS}) re-skin the page's score inputs — the
 * marketing wallet, the daily spend limit and how many suggestions are queued — so
 * the gauge visibly cycles through different states (Optimal / Sustainable / Poor,
 * including the 10% floor) as the brand switcher changes. The page maps the active
 * brand to one of these by its list position (see `pages/acquisition/index.tsx`).
 * Delete the profiles alongside this file once the API provides real per-brand
 * figures.
 */
import type {
  AcquisitionData,
  Campaign,
  CampaignFilter,
  CampaignStatus,
  Insights,
  LumioChannels,
  PerformanceOverview,
  Recommendation,
  SelfManagedChannels,
} from './types';
import {
  acquisitionActions,
  acquisitionHeading,
  acquisitionScoreSummary,
  computeAcquisitionScore,
  type AcquisitionActionId,
} from '../pages/acquisition/acquisition-score';

const HALLOWEEN_IMG = '/assets/marketing/campaign-halloween.png';

// Mock suggestion expiry deadlines as `now + offset`, captured once at load time,
// so the "Expires in" timers count down live against the browser clock. Returns
// epoch milliseconds (see Campaign.expiresAt). The offsets below straddle the
// urgency thresholds (red < 2 days, gold < 5 days, grey beyond) so each tone shows.
const expiresIn = (d: number, h: number, m: number, s = 0): number =>
  Date.now() + (((d * 24 + h) * 60 + m) * 60 + s) * 1000;

/** Format a dollar figure for the budget tiles. Shows cents only when the value has a
 *  fractional part, so whole dollars stay clean (1200 → "$1,200") but exact amounts
 *  are never rounded away (3.75 → "$3.75", not "$4"). */
const usd = (n: number): string =>
  `$${n.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

// AI suggestions card — the two shortcuts map onto the two score levers ("Increase
// daily budget" → budget runway, "Activate a new campaign" → campaign queue).
const RECOMMENDATIONS: Recommendation[] = [
  {
    id: 'increase-daily-budget',
    label: 'Increase daily budget',
    icon: 'sparkle',
    ctaIcon: 'arrow-right-circle',
    href: '',
  },
  {
    id: 'activate-new-campaign',
    label: 'Activate a new campaign',
    icon: 'sparkle',
    ctaIcon: 'arrow-right-circle',
    href: '',
  },
];

// Managed (Lumio) acquisition channels with budget pacing + CPA.
const LUMIO_CHANNELS: LumioChannels = {
  channels: [
    {
      id: 'web-networks',
      name: 'Web Networks',
      icon: 'globe',
      spent: 42,
      budget: 50,
      progress: 84,
      progressColor: 'info',
      cpa: '$8.2 CPA',
      state: 'active',
      badge: null,
    },
    {
      id: 'app-networks',
      name: 'App Networks',
      icon: 'mobile',
      // Gated channel (release feature flag): modelled NEUTRAL — no colour-coded health
      // state, no alarm badge, a calm partial bar — so nothing grabs attention behind the
      // frosted lock. Only Web reacts to health colour coding (real data later). See
      // `config/acquisitionChannels`.
      spent: 30,
      budget: 50,
      progress: 60,
      progressColor: 'info',
      cpa: '$18.5 CPA',
      state: 'active',
      badge: null,
    },
    {
      id: 'crypto-sites',
      name: 'Crypto Sites',
      icon: 'crypto',
      spent: 14,
      budget: 20,
      progress: 70,
      progressColor: 'info',
      cpa: '$24.3 CPA',
      state: 'active',
      badge: null,
    },
    {
      id: 'short-videos',
      name: 'Short Videos',
      icon: 'video',
      // Gated channel (release feature flag): modelled NEUTRAL — see App Networks above.
      spent: 38,
      budget: 50,
      progress: 76,
      progressColor: 'info',
      cpa: '$11.4 CPA',
      state: 'active',
      badge: null,
    },
  ],
};

// Self-managed (non-Lumio) channels.
// Self-managed channels. Only Affiliates today (the Influencers entrypoint was
// removed); the count is a placeholder overridden with the live affiliate count on
// the acquisition page (see useAffiliateResource + listAffiliates in index.tsx).
const SELF_MANAGED: SelfManagedChannels = {
  channels: [{ id: 'affiliates', icon: 'affiliates', count: 0, label: 'Affiliates' }],
};

const FILTERS: CampaignFilter[] = [
  { value: 'seasonal' },
  { value: 'sports' },
  { value: 'promo' },
  { value: 'acquisition' },
];

// Suggestions — the Lumio-recommended campaigns queued for activation. Show an
// "Expires in" countdown. This is the FULL queue (a superset); per brand the page
// shows only the first N of them (see ACQ_BRAND_VARIANTS), and the score's
// campaign half is driven by how many are shown. The offsets straddle the urgency
// thresholds so all three timer tones appear in any reasonably-long slice.
const SUGGESTIONS: Campaign[] = [
  {
    id: 's1',
    title: 'Halloween is coming, trick or treat for players!',
    image: HALLOWEEN_IMG,
    period: '20-30 Oct',
    status: 'suggestion',
    category: 'seasonal',
    expiresAt: expiresIn(0, 0, 56, 21),
    channels: [],
  }, // red — under an hour
  {
    id: 's2',
    title: 'Welcome 200% Bonus for new players',
    image: HALLOWEEN_IMG,
    period: '1-31 Oct',
    status: 'suggestion',
    category: 'acquisition',
    expiresAt: expiresIn(3, 2, 14, 8),
    channels: [],
  }, // gold — ~3 days
  {
    id: 's3',
    title: 'Copa America Freebet weekend',
    image: HALLOWEEN_IMG,
    period: '14-20 Oct',
    status: 'suggestion',
    category: 'sports',
    expiresAt: expiresIn(6, 11, 42, 55),
    channels: [],
  }, // grey — ~6 days
  {
    id: 's4',
    title: 'Friday Reload 50% up to $100',
    image: HALLOWEEN_IMG,
    period: 'every Friday',
    status: 'suggestion',
    category: 'promo',
    expiresAt: expiresIn(4, 4, 30),
    channels: [],
  }, // gold — ~4 days
  {
    id: 's5',
    title: 'Diwali Festival of Lights bonus',
    image: HALLOWEEN_IMG,
    period: '1-5 Nov',
    status: 'suggestion',
    category: 'seasonal',
    expiresAt: expiresIn(1, 6, 12),
    channels: [],
  }, // red — ~1.25 days
  {
    id: 's6',
    title: 'NBA Opening Night odds boost',
    image: HALLOWEEN_IMG,
    period: '22 Oct',
    status: 'suggestion',
    category: 'sports',
    expiresAt: expiresIn(5, 8, 5),
    channels: [],
  }, // grey — ~5 days
  {
    id: 's7',
    title: 'Refer a Friend, $25 for each',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'suggestion',
    category: 'acquisition',
    expiresAt: expiresIn(0, 3, 20),
    channels: [],
  }, // red — ~3 hours
  {
    id: 's8',
    title: 'Black Friday Mega Deposit match',
    image: HALLOWEEN_IMG,
    period: '24-30 Nov',
    status: 'suggestion',
    category: 'promo',
    expiresAt: expiresIn(2, 10, 0),
    channels: [],
  }, // gold — ~2.4 days
  {
    id: 's9',
    title: 'Singles Day Mega Sale free spins',
    image: HALLOWEEN_IMG,
    period: '11 Nov',
    status: 'suggestion',
    category: 'seasonal',
    expiresAt: expiresIn(1, 20, 0),
    channels: [],
  }, // red — ~1.8 days
  {
    id: 's10',
    title: 'UEFA Nations League acca boost',
    image: HALLOWEEN_IMG,
    period: 'Oct weekends',
    status: 'suggestion',
    category: 'sports',
    expiresAt: expiresIn(4, 12, 0),
    channels: [],
  }, // gold — ~4.5 days
  {
    id: 's11',
    title: 'Midnight Madness reload 40%',
    image: HALLOWEEN_IMG,
    period: 'nightly',
    status: 'suggestion',
    category: 'promo',
    expiresAt: expiresIn(0, 6, 30),
    channels: [],
  }, // red — ~6.5 hours
  {
    id: 's12',
    title: 'Thanksgiving Turkey Spins giveaway',
    image: HALLOWEEN_IMG,
    period: '28 Nov',
    status: 'suggestion',
    category: 'seasonal',
    expiresAt: expiresIn(5, 2, 0),
    channels: [],
  }, // grey — ~5 days
];

// Established campaigns (running / paused / ended) — the same for every brand. The
// page always renders these in full; only the suggestion slice varies per brand.
// Some carry a `linkKey` matching an operations promotion's `linkKey` in
// `operations.ts`, which lets the operations promotion detail deep-link here via
// its "also in use for Acquisition" crosslink (see types.ts `Campaign.linkKey`).
const ESTABLISHED: Campaign[] = [
  // Running — show a LIVE badge, no footer.
  {
    id: 'r1',
    title: 'Champions League Acca Boost',
    image: HALLOWEEN_IMG,
    period: '1-31 Oct',
    status: 'running',
    category: 'sports',
    channels: [],
  },
  {
    id: 'r2',
    title: 'Weekend Cashback 20%',
    image: HALLOWEEN_IMG,
    period: 'Sat-Sun',
    status: 'running',
    category: 'promo',
    channels: [],
  },
  {
    id: 'r3',
    title: 'Lucky Spin Tuesdays free spins',
    image: HALLOWEEN_IMG,
    period: 'every Tuesday',
    status: 'running',
    category: 'promo',
    channels: [],
  },
  {
    id: 'r4',
    title: 'Daily Free Spins Carousel',
    image: HALLOWEEN_IMG,
    period: 'daily',
    status: 'running',
    category: 'promo',
    channels: [],
  },
  {
    id: 'r5',
    title: 'Premier League Bet & Get',
    image: HALLOWEEN_IMG,
    period: 'Saturdays',
    status: 'running',
    category: 'sports',
    channels: [],
  },
  // Renamed to match its operations promotion counterpart (rn-reactivation) so
  // the "also in use for Acquisition" crosslink lands on the same-named campaign.
  {
    id: 'r6',
    title: 'Weekend Reactivation push for sleepers',
    image: HALLOWEEN_IMG,
    period: 'Sat-Sun',
    status: 'running',
    category: 'acquisition',
    linkKey: 'weekend-reactivation',
    channels: [],
  },
  // Paused — "Paused on <when>".
  {
    id: 'p1',
    title: 'Summer Splash free spins drop',
    image: HALLOWEEN_IMG,
    period: '1-30 Jun',
    status: 'paused',
    category: 'seasonal',
    statusAt: 'Sep 12, 09:20',
    linkKey: 'summer-splash',
    channels: [],
  },
  {
    id: 'p2',
    title: 'VIP Reload Weekend exclusive',
    image: HALLOWEEN_IMG,
    period: '10-12 Aug',
    status: 'paused',
    category: 'promo',
    statusAt: 'Aug 30, 18:45',
    channels: [],
  },
  {
    id: 'p3',
    title: 'Back to School deposit bonus',
    image: HALLOWEEN_IMG,
    period: '1-7 Sep',
    status: 'paused',
    category: 'acquisition',
    statusAt: 'Sep 02, 11:10',
    linkKey: 'back-to-school',
    channels: [],
  },
  {
    id: 'p4',
    title: 'Midweek Madness odds boost',
    image: HALLOWEEN_IMG,
    period: 'every Wed',
    status: 'paused',
    category: 'sports',
    statusAt: 'Sep 18, 16:05',
    linkKey: 'midweek-madness',
    channels: [],
  },
  // Paused but already past its end deadline → treated as an ENDED campaign
  // (shows the "Ended on" tag, cannot be resumed). Demonstrates the expiry
  // rule in utils/campaign-status; `expiresIn` with a negative offset = past.
  {
    id: 'p5',
    title: 'Easter Egg Hunt giveaway',
    image: HALLOWEEN_IMG,
    period: '1-10 Apr',
    status: 'paused',
    category: 'seasonal',
    statusAt: 'Apr 11, 08:00',
    expiresAt: expiresIn(-20, 0, 0),
    channels: [],
  },
  {
    id: 'p6',
    title: 'High Roller Cashback monthly',
    image: HALLOWEEN_IMG,
    period: 'monthly',
    status: 'paused',
    category: 'promo',
    statusAt: 'Sep 01, 12:30',
    channels: [],
  },
  {
    id: 'p7',
    title: 'World Cup Qualifier specials',
    image: HALLOWEEN_IMG,
    period: '5-12 Sep',
    status: 'paused',
    category: 'sports',
    statusAt: 'Sep 13, 22:15',
    channels: [],
  },
  {
    id: 'p8',
    title: 'Welcome Pack 100 free spins',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'paused',
    category: 'acquisition',
    statusAt: 'Aug 20, 10:00',
    channels: [],
  },
  {
    id: 'p9',
    title: 'Halloween Spooky Spins drop',
    image: HALLOWEEN_IMG,
    period: '25-31 Oct',
    status: 'paused',
    category: 'seasonal',
    statusAt: 'Nov 01, 09:00',
    channels: [],
  },
  {
    id: 'p10',
    title: 'Tuesday Tournament series',
    image: HALLOWEEN_IMG,
    period: 'every Tue',
    status: 'paused',
    category: 'promo',
    statusAt: 'Sep 24, 14:40',
    channels: [],
  },
  {
    id: 'p11',
    title: 'Winter Wonderland free spins',
    image: HALLOWEEN_IMG,
    period: '1-31 Dec',
    status: 'paused',
    category: 'seasonal',
    statusAt: 'Dec 28, 10:15',
    channels: [],
  },
  {
    id: 'p12',
    title: 'NHL Faceoff odds boost',
    image: HALLOWEEN_IMG,
    period: '8-14 Oct',
    status: 'paused',
    category: 'sports',
    statusAt: 'Oct 15, 19:30',
    channels: [],
  },
  {
    id: 'p13',
    title: 'Happy Hour deposit match',
    image: HALLOWEEN_IMG,
    period: 'weekdays 6-8pm',
    status: 'paused',
    category: 'promo',
    statusAt: 'Sep 27, 20:05',
    channels: [],
  },
  {
    id: 'p14',
    title: 'First Bet Safety Net $20',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'paused',
    category: 'acquisition',
    statusAt: 'Sep 05, 09:45',
    channels: [],
  },
  {
    id: 'p15',
    title: 'Spring Bloom bonus drop',
    image: HALLOWEEN_IMG,
    period: '1-20 Mar',
    status: 'paused',
    category: 'seasonal',
    statusAt: 'Mar 21, 08:30',
    channels: [],
  },
  {
    id: 'p16',
    title: 'Formula 1 Grand Prix specials',
    image: HALLOWEEN_IMG,
    period: 'race weekends',
    status: 'paused',
    category: 'sports',
    statusAt: 'Sep 22, 17:20',
    channels: [],
  },
  {
    id: 'p17',
    title: 'Weekend Warrior reload 30%',
    image: HALLOWEEN_IMG,
    period: 'Sat-Sun',
    status: 'paused',
    category: 'promo',
    statusAt: 'Sep 29, 13:10',
    channels: [],
  },
  {
    id: 'p18',
    title: 'Sign Up & Spin welcome offer',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'paused',
    category: 'acquisition',
    statusAt: 'Aug 14, 11:55',
    channels: [],
  },
  {
    id: 'p19',
    title: 'Oktoberfest cashback fest',
    image: HALLOWEEN_IMG,
    period: '16 Sep-3 Oct',
    status: 'paused',
    category: 'seasonal',
    statusAt: 'Oct 04, 15:00',
    channels: [],
  },
  {
    id: 'p20',
    title: 'Tennis Grand Slam acca boost',
    image: HALLOWEEN_IMG,
    period: '26 Aug-8 Sep',
    status: 'paused',
    category: 'sports',
    statusAt: 'Sep 09, 21:40',
    channels: [],
  },
  {
    id: 'p21',
    title: 'Mystery Box Mondays',
    image: HALLOWEEN_IMG,
    period: 'every Mon',
    status: 'paused',
    category: 'promo',
    statusAt: 'Sep 23, 12:00',
    channels: [],
  },
  {
    id: 'p22',
    title: 'Refer a Friend double bonus',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'paused',
    category: 'acquisition',
    statusAt: 'Aug 28, 10:25',
    channels: [],
  },
  {
    id: 'p23',
    title: 'Cinco de Mayo fiesta spins',
    image: HALLOWEEN_IMG,
    period: '1-5 May',
    status: 'paused',
    category: 'seasonal',
    statusAt: 'May 06, 09:15',
    channels: [],
  },
  {
    id: 'p24',
    title: 'Cricket World Cup bet & get',
    image: HALLOWEEN_IMG,
    period: '5 Oct-19 Nov',
    status: 'paused',
    category: 'sports',
    statusAt: 'Nov 20, 22:30',
    channels: [],
  },
  {
    id: 'p25',
    title: 'Thursday Thunder free spins',
    image: HALLOWEEN_IMG,
    period: 'every Thu',
    status: 'paused',
    category: 'promo',
    statusAt: 'Sep 26, 14:50',
    channels: [],
  },
  {
    id: 'p26',
    title: 'New Player Cashback Week',
    image: HALLOWEEN_IMG,
    period: 'first 7 days',
    status: 'paused',
    category: 'acquisition',
    statusAt: 'Sep 03, 16:40',
    channels: [],
  },
  {
    id: 'p27',
    title: 'Summer Carnival jackpot',
    image: HALLOWEEN_IMG,
    period: '1-31 Aug',
    status: 'paused',
    category: 'seasonal',
    statusAt: 'Aug 31, 23:50',
    channels: [],
  },
  {
    id: 'p28',
    title: 'Golf Major weekend specials',
    image: HALLOWEEN_IMG,
    period: 'Thu-Sun',
    status: 'paused',
    category: 'sports',
    statusAt: 'Sep 16, 18:10',
    channels: [],
  },
  {
    id: 'p29',
    title: 'Lucky Sevens slots race',
    image: HALLOWEEN_IMG,
    period: '7-14 Sep',
    status: 'paused',
    category: 'promo',
    statusAt: 'Sep 15, 11:30',
    channels: [],
  },
  {
    id: 'p30',
    title: 'Welcome Bonus 300% match',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'paused',
    category: 'acquisition',
    statusAt: 'Aug 09, 10:00',
    channels: [],
  },
  {
    id: 'p31',
    title: 'Harvest Festival free spins',
    image: HALLOWEEN_IMG,
    period: '20-30 Sep',
    status: 'paused',
    category: 'seasonal',
    statusAt: 'Oct 01, 08:45',
    channels: [],
  },
  {
    id: 'p32',
    title: 'Boxing Title Fight odds boost',
    image: HALLOWEEN_IMG,
    period: '14 Sep',
    status: 'paused',
    category: 'sports',
    statusAt: 'Sep 15, 02:20',
    channels: [],
  },
  {
    id: 'p33',
    title: 'Saturday Slots Showdown',
    image: HALLOWEEN_IMG,
    period: 'every Sat',
    status: 'paused',
    category: 'promo',
    statusAt: 'Sep 28, 13:35',
    channels: [],
  },
  {
    id: 'p34',
    title: 'Deposit & Double first time',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'paused',
    category: 'acquisition',
    statusAt: 'Aug 22, 09:30',
    channels: [],
  },
  {
    id: 'p35',
    title: 'Lunar New Year red envelopes',
    image: HALLOWEEN_IMG,
    period: '10-17 Feb',
    status: 'paused',
    category: 'seasonal',
    statusAt: 'Feb 18, 12:10',
    channels: [],
  },
  {
    id: 'p36',
    title: 'Rugby Championship bet boost',
    image: HALLOWEEN_IMG,
    period: 'Sep weekends',
    status: 'paused',
    category: 'sports',
    statusAt: 'Sep 30, 19:55',
    channels: [],
  },
  // Ended — "Ended on <when>".
  {
    id: 'e1',
    title: 'Euro 2024 Special acca insurance',
    image: HALLOWEEN_IMG,
    period: '14 Jun-14 Jul',
    status: 'ended',
    category: 'sports',
    statusAt: 'Jul 14, 23:00',
    linkKey: 'euro-special-acca',
    channels: [],
  },
  {
    id: 'e2',
    title: 'Spring Jackpot Race $50k pool',
    image: HALLOWEEN_IMG,
    period: '1-31 May',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'May 31, 21:30',
    linkKey: 'spring-jackpot-race',
    channels: [],
  },
  {
    id: 'e3',
    title: 'New Year Mega Drop celebration',
    image: HALLOWEEN_IMG,
    period: '31 Dec-1 Jan',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Jan 02, 02:00',
    linkKey: 'new-year-mega-drop',
    channels: [],
  },
  {
    id: 'e4',
    title: "Valentine's Match Bonus for couples",
    image: HALLOWEEN_IMG,
    period: '10-15 Feb',
    status: 'ended',
    category: 'promo',
    statusAt: 'Feb 15, 12:00',
    linkKey: 'valentines-match-bonus',
    channels: [],
  },
  {
    id: 'e5',
    title: 'Summer Sizzler jackpot race',
    image: HALLOWEEN_IMG,
    period: '1-31 Jul',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Jul 31, 23:59',
    channels: [],
  },
  {
    id: 'e6',
    title: 'Wimbledon Match Bonus fortnight',
    image: HALLOWEEN_IMG,
    period: '1-14 Jul',
    status: 'ended',
    category: 'sports',
    statusAt: 'Jul 14, 20:00',
    linkKey: 'wimbledon-match-bonus',
    channels: [],
  },
  {
    id: 'e7',
    title: 'Refer & Earn Spring drive',
    image: HALLOWEEN_IMG,
    period: '1-30 Apr',
    status: 'ended',
    category: 'acquisition',
    statusAt: 'Apr 30, 18:00',
    linkKey: 'refer-earn-spring',
    channels: [],
  },
  {
    id: 'e8',
    title: 'Carnival Cashback fiesta',
    image: HALLOWEEN_IMG,
    period: '10-20 Feb',
    status: 'ended',
    category: 'promo',
    statusAt: 'Feb 20, 21:00',
    channels: [],
  },
  {
    id: 'e9',
    title: 'St. Patrick Lucky Bonus',
    image: HALLOWEEN_IMG,
    period: '15-18 Mar',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Mar 18, 23:30',
    channels: [],
  },
  {
    id: 'e10',
    title: 'NFL Kickoff free bet weekend',
    image: HALLOWEEN_IMG,
    period: '5-9 Sep',
    status: 'ended',
    category: 'sports',
    statusAt: 'Sep 09, 19:45',
    channels: [],
  },
  {
    id: 'e11',
    title: 'Boxing Day Bonanza free spins',
    image: HALLOWEEN_IMG,
    period: '26 Dec',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Dec 27, 10:00',
    channels: [],
  },
  {
    id: 'e12',
    title: 'Super Bowl Sunday odds boost',
    image: HALLOWEEN_IMG,
    period: '11 Feb',
    status: 'ended',
    category: 'sports',
    statusAt: 'Feb 12, 04:30',
    channels: [],
  },
  {
    id: 'e13',
    title: 'Midnight Reload 40% match',
    image: HALLOWEEN_IMG,
    period: 'nightly',
    status: 'ended',
    category: 'promo',
    statusAt: 'Aug 31, 23:59',
    channels: [],
  },
  {
    id: 'e14',
    title: 'First Deposit Triple Boost',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'ended',
    category: 'acquisition',
    statusAt: 'Jul 31, 18:00',
    channels: [],
  },
  {
    id: 'e15',
    title: 'Autumn Leaves jackpot drop',
    image: HALLOWEEN_IMG,
    period: '1-30 Sep',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Sep 30, 22:00',
    channels: [],
  },
  {
    id: 'e16',
    title: 'Masters Golf bet & get',
    image: HALLOWEEN_IMG,
    period: '11-14 Apr',
    status: 'ended',
    category: 'sports',
    statusAt: 'Apr 14, 23:00',
    channels: [],
  },
  {
    id: 'e17',
    title: 'Monday Blues cashback 25%',
    image: HALLOWEEN_IMG,
    period: 'every Mon',
    status: 'ended',
    category: 'promo',
    statusAt: 'Aug 26, 13:00',
    channels: [],
  },
  {
    id: 'e18',
    title: 'Welcome Wheel mega spin',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'ended',
    category: 'acquisition',
    statusAt: 'Jun 30, 20:15',
    channels: [],
  },
  {
    id: 'e19',
    title: 'Independence Day fireworks spins',
    image: HALLOWEEN_IMG,
    period: '1-4 Jul',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Jul 05, 09:00',
    channels: [],
  },
  {
    id: 'e20',
    title: 'NBA Finals series boost',
    image: HALLOWEEN_IMG,
    period: '6-20 Jun',
    status: 'ended',
    category: 'sports',
    statusAt: 'Jun 21, 03:45',
    channels: [],
  },
  {
    id: 'e21',
    title: 'Flash Friday free spins',
    image: HALLOWEEN_IMG,
    period: 'every Fri',
    status: 'ended',
    category: 'promo',
    statusAt: 'Aug 30, 21:00',
    channels: [],
  },
  {
    id: 'e22',
    title: 'Refer & Earn Summer drive',
    image: HALLOWEEN_IMG,
    period: '1-30 Jun',
    status: 'ended',
    category: 'acquisition',
    statusAt: 'Jun 30, 18:00',
    channels: [],
  },
  {
    id: 'e23',
    title: 'Mardi Gras masquerade bonus',
    image: HALLOWEEN_IMG,
    period: '9-13 Feb',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Feb 14, 08:00',
    channels: [],
  },
  {
    id: 'e24',
    title: 'Champions League Final special',
    image: HALLOWEEN_IMG,
    period: '1 Jun',
    status: 'ended',
    category: 'sports',
    statusAt: 'Jun 02, 01:30',
    channels: [],
  },
  {
    id: 'e25',
    title: 'Slots Marathon weekend race',
    image: HALLOWEEN_IMG,
    period: '13-14 Jul',
    status: 'ended',
    category: 'promo',
    statusAt: 'Jul 15, 10:00',
    channels: [],
  },
  {
    id: 'e26',
    title: 'New Player 50 free spins',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'ended',
    category: 'acquisition',
    statusAt: 'May 31, 16:30',
    channels: [],
  },
  {
    id: 'e27',
    title: 'Cherry Blossom spring spins',
    image: HALLOWEEN_IMG,
    period: '1-15 Apr',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Apr 16, 09:30',
    channels: [],
  },
  {
    id: 'e28',
    title: 'Wimbledon Finals bet boost',
    image: HALLOWEEN_IMG,
    period: '13-14 Jul',
    status: 'ended',
    category: 'sports',
    statusAt: 'Jul 15, 20:00',
    channels: [],
  },
  {
    id: 'e29',
    title: 'Tuesday Treats deposit match',
    image: HALLOWEEN_IMG,
    period: 'every Tue',
    status: 'ended',
    category: 'promo',
    statusAt: 'Aug 27, 12:30',
    channels: [],
  },
  {
    id: 'e30',
    title: 'Big Welcome 200 free spins',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'ended',
    category: 'acquisition',
    statusAt: 'Apr 30, 17:45',
    channels: [],
  },
  {
    id: 'e31',
    title: 'Thanksgiving Feast bonus',
    image: HALLOWEEN_IMG,
    period: '22-28 Nov',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Nov 29, 10:00',
    channels: [],
  },
  {
    id: 'e32',
    title: 'Ryder Cup head-to-head boost',
    image: HALLOWEEN_IMG,
    period: '27-29 Sep',
    status: 'ended',
    category: 'sports',
    statusAt: 'Sep 30, 21:15',
    channels: [],
  },
  {
    id: 'e33',
    title: 'Wednesday Wins cashback',
    image: HALLOWEEN_IMG,
    period: 'every Wed',
    status: 'ended',
    category: 'promo',
    statusAt: 'Aug 28, 14:00',
    channels: [],
  },
  {
    id: 'e34',
    title: 'Sign Up Spin & Win',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'ended',
    category: 'acquisition',
    statusAt: 'Mar 31, 19:00',
    channels: [],
  },
  {
    id: 'e35',
    title: 'Holi Festival of Colours bonus',
    image: HALLOWEEN_IMG,
    period: '24-25 Mar',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Mar 26, 08:30',
    channels: [],
  },
  {
    id: 'e36',
    title: 'Six Nations Rugby acca boost',
    image: HALLOWEEN_IMG,
    period: '1 Feb-16 Mar',
    status: 'ended',
    category: 'sports',
    statusAt: 'Mar 17, 22:00',
    channels: [],
  },
  {
    id: 'e37',
    title: 'Sunday Funday free spins',
    image: HALLOWEEN_IMG,
    period: 'every Sun',
    status: 'ended',
    category: 'promo',
    statusAt: 'Aug 25, 15:30',
    channels: [],
  },
  {
    id: 'e38',
    title: 'First Bet $30 bonus back',
    image: HALLOWEEN_IMG,
    period: 'ongoing',
    status: 'ended',
    category: 'acquisition',
    statusAt: 'Feb 28, 18:00',
    channels: [],
  },
  {
    id: 'e39',
    title: 'Octoberfest beer & spins',
    image: HALLOWEEN_IMG,
    period: '20 Sep-5 Oct',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Oct 06, 09:00',
    channels: [],
  },
  {
    id: 'e40',
    title: 'Tour de France stage boost',
    image: HALLOWEEN_IMG,
    period: '29 Jun-21 Jul',
    status: 'ended',
    category: 'sports',
    statusAt: 'Jul 22, 20:30',
    channels: [],
  },
  {
    id: 'e41',
    title: 'Payday Reload 60% match',
    image: HALLOWEEN_IMG,
    period: 'month-end',
    status: 'ended',
    category: 'promo',
    statusAt: 'Aug 31, 23:00',
    channels: [],
  },
  {
    id: 'e42',
    title: 'Welcome Cashback first week',
    image: HALLOWEEN_IMG,
    period: 'first 7 days',
    status: 'ended',
    category: 'acquisition',
    statusAt: 'Jan 31, 17:00',
    channels: [],
  },
  {
    id: 'e43',
    title: 'Christmas Eve Mega Drop',
    image: HALLOWEEN_IMG,
    period: '24 Dec',
    status: 'ended',
    category: 'seasonal',
    statusAt: 'Dec 25, 00:30',
    channels: [],
  },
];

/** The full campaign pool (every suggestion + every established campaign) — used by
 *  the detail pages to resolve any campaign by id / linkKey regardless of which
 *  brand slice was on screen. */
const FULL_POOL: Campaign[] = [...SUGGESTIONS, ...ESTABLISHED];

const countOf = (status: CampaignStatus): number => ESTABLISHED.filter((c) => c.status === status).length;

// Bottom KPI grid: each tile shows two figures.
//
// BOTH columns are now sourced LIVE from the Stats API, harmonised with the Home/Stats
// range selector: the left column is the selected range, the right column its "next
// step up" range (see `useAcquisitionPerformance` / `performance-stats`). So these
// `today` / `month` values are no longer rendered for the wired KPIs; they remain as
// documentation of the intended figures and as the fallback for any KPI id without an
// API spec. The three ad-spend KPIs (Blended CPA, Budget spent, ROI) have no stats
// field and render `?` in both columns. See `missing-data.md`.
const PERFORMANCE_OVERVIEW: PerformanceOverview = {
  kpis: [
    {
      id: 'blended-cpa',
      title: 'Blended CPA',
      subtitle: 'Cost per acquisition',
      today: { value: '$3.6', change: '+85%', changeType: 'increase' },
      month: { value: '$3.5', change: '-12%', changeType: 'decrease' },
    },
    {
      id: 'budget-spent',
      title: 'Budget spent',
      subtitle: 'Total acquisition spend',
      today: { value: '$80', change: '+15%', changeType: 'increase' },
      month: { value: '$2,140', change: '+18%', changeType: 'increase' },
    },
    {
      id: 'registrations',
      title: 'Registrations',
      subtitle: 'New players joining your website',
      today: { value: '50', change: '+85%', changeType: 'increase' },
      month: { value: '1,280', change: '+34%', changeType: 'increase' },
    },
    {
      id: 'ftds',
      title: 'FTDs',
      subtitle: 'Number of First Time Deposits',
      today: { value: '22', change: '+15%', changeType: 'increase' },
      month: { value: '580', change: '+28%', changeType: 'increase' },
    },
    {
      id: 'ftd-deposits',
      title: 'FTD Deposits',
      subtitle: 'Total deposited by FTDs',
      today: { value: '$1,188', change: '+22%', changeType: 'increase' },
      month: { value: '$30,160', change: '+31%', changeType: 'increase' },
    },
    {
      id: 'avg-deposit-ftd',
      title: 'Avg Deposit / FTD',
      subtitle: 'Average first deposit',
      today: { value: '$54', change: '+22%', changeType: 'increase' },
      month: { value: '$52', change: '+31%', changeType: 'increase' },
    },
    {
      id: 'total-turnover',
      title: 'Total Turnover',
      subtitle: 'Money wagered by players',
      today: { value: '$4,200', change: '+32%', changeType: 'increase' },
      month: { value: '$142,000', change: '+42%', changeType: 'increase' },
    },
    {
      id: 'turnover-ftd',
      title: 'Turnover / FTD',
      subtitle: 'Wagered per depositor',
      today: { value: '$191', change: '+14%', changeType: 'increase' },
      month: { value: '$245', change: '+8%', changeType: 'increase' },
    },
    {
      id: 'ggr',
      title: 'GGR',
      subtitle: 'Gross Gaming Revenue',
      today: { value: '$860', change: '+24%', changeType: 'increase' },
      month: { value: '$24,800', change: '+31%', changeType: 'increase' },
    },
    {
      id: 'roi',
      title: 'ROI',
      subtitle: 'Return on ad spend',
      today: { value: '975%', change: '+14%', changeType: 'increase' },
      month: { value: '1,059%', change: '+8%', changeType: 'increase' },
    },
  ],
};

/** Which of the {@link ACQ_BRAND_VARIANTS} datasets a brand renders. */
export type AcqBrandVariant = 0 | 1 | 2 | 3 | 4;

/**
 * Real budget figures (from the acquisition-budget API) that override the mock
 * profile's wallet + daily cap when the page has them loaded. The queued-suggestion
 * count is NOT overridden (no campaign-queue API yet), so the campaign half of the
 * score stays mock.
 */
export type BudgetOverride = {
  /** Marketing wallet balance ($). */
  availableBudget: number;
  /** Daily ad-spend cap ($). Omitted when the API has no value yet → mock daily is used. */
  dailySpendLimit?: number;
};

type AcqBrandProfile = {
  /** Marketing wallet balance ($). */
  availableBudget: number;
  /** Daily ad-spend cap ($). Runway = availableBudget / dailySpendLimit. */
  dailySpendLimit: number;
  /** How many of {@link SUGGESTIONS} sit in this brand's queue (the first N). */
  suggestions: number;
};

/**
 * Stand-in brand datasets, chosen so cycling the brand switcher walks the hero
 * gauge through visibly-distinct score states AND every hero-action combination
 * (none / budget-only / campaign-only / both). The resulting scores + actions are
 * pinned in `acquisition-score.test.ts`; full worked examples live in
 * `docs/acquisition-score.md`.
 *
 *   0 — 4d runway, 2 queued  → 90, "Optimal" (blue ring)   · actions: none (both halves good)
 *   1 — 3d runway, 7 queued  → 65, "Sustainable" (yellow)  · actions: Activate a new campaign
 *   2 — 0.5d runway, 1 queued → 57, "Poor" (red ring)      · actions: Increase daily budget
 *   3 — 0.15d runway, 11 queued → 10, the score floor (red) · actions: BOTH — also demos the
 *                                   "10+ queued = 0 campaign points" cap
 *   4 — 6d runway, 1 queued  → 95, "Optimal" (green ring)  · actions: none (near-ideal)
 *
 * With only 3 brands the cycle already shows green/yellow/red and the none/one/one
 * action states (variants 0–2). Delete alongside this file once a real per-brand
 * API lands.
 */
export const ACQ_BRAND_VARIANTS: readonly AcqBrandProfile[] = [
  { availableBudget: 600, dailySpendLimit: 150, suggestions: 2 },
  { availableBudget: 600, dailySpendLimit: 200, suggestions: 7 },
  { availableBudget: 75, dailySpendLimit: 150, suggestions: 1 },
  { availableBudget: 30, dailySpendLimit: 200, suggestions: 11 },
  { availableBudget: 900, dailySpendLimit: 150, suggestions: 1 },
];

/**
 * Build the Acquisition page dataset for a brand `variant`. The hero gauge's score
 * is computed from the queued-suggestion count + budget runway via
 * {@link computeAcquisitionScore} (the page reads it straight off
 * `insights.summary`), and the budget tiles are formatted from the same numbers,
 * so the gauge, the Suggestions tab badge and the budget card always agree.
 *
 * `variant` (0–4, default 0) selects an {@link ACQ_BRAND_VARIANTS} profile. Pass
 * `budget` to feed the REAL acquisition-budget (wallet + daily cap) into the budget
 * half of the score and the budget card; without it the mock profile's budget is
 * used.
 *
 * Pass `suggestedCount` (the live count of suggested campaigns the browser shows)
 * to feed the REAL campaign queue into the score's campaign half — so the gauge, the
 * hero heading and the "Activate a new campaign" suggestion all reflect the actual
 * queue rather than a mock figure. An empty queue scores full marks (nothing to
 * action); the count climbs the score down only once a real backlog exists. Omit
 * `suggestedCount` (the canonical dataset has no live context) and the mock profile
 * queue stands.
 */
export function buildAcquisitionData(
  variant: AcqBrandVariant = 0,
  budget?: BudgetOverride,
  suggestedCount?: number,
): AcquisitionData {
  const profile = ACQ_BRAND_VARIANTS[variant] ?? ACQ_BRAND_VARIANTS[0];
  const suggestions = SUGGESTIONS.slice(0, profile.suggestions);
  const pool: Campaign[] = [...suggestions, ...ESTABLISHED];

  // Real budget overrides the mock profile's when the page passes it in; both the
  // score's budget half and the budget tiles read these, so they never disagree.
  const availableBudget = budget?.availableBudget ?? profile.availableBudget;
  const dailySpendLimit = budget?.dailySpendLimit ?? profile.dailySpendLimit;

  // The campaign half reads the REAL queued-suggestion count when the page supplies
  // it (the live browser pool); the mock profile count is only a fallback for the
  // canonical dataset, which has no live context.
  const queuedSuggestions = suggestedCount ?? suggestions.length;

  const breakdown = computeAcquisitionScore({
    queuedSuggestions,
    availableBudget,
    dailySpendLimit,
  });
  const { score } = breakdown;
  const { status, tone } = acquisitionScoreSummary(score);
  // The hero heading reflects the actual state (which lever, if any, is lacking),
  // not just the score band — more informative than acquisitionScoreSummary's
  // generic band copy.
  const title = acquisitionHeading(breakdown);

  // Surface only the recommendation(s) for the half/halves that are lacking
  // ("Poor"); when both halves are good the card shows no suggestions at all.
  const visible = new Set<AcquisitionActionId>(acquisitionActions(breakdown));
  const recommendations = RECOMMENDATIONS.filter((r) => visible.has(r.id as AcquisitionActionId));

  const insights: Insights = {
    // The ring fills to the score, so progress === score.
    summary: { title, score, status, tone, progress: score },
    recommendations,
  };

  return {
    header: { title: 'Acquisition' },
    insights,
    lumioChannels: LUMIO_CHANNELS,
    budgets: {
      availableBudget,
      dailySpendLimit,
      items: [
        { id: 'remaining-budget', icon: 'wallet', value: usd(availableBudget), label: 'Remaining Budget' },
        { id: 'daily-limit', icon: 'calendar', value: usd(dailySpendLimit), label: 'Daily Limit' },
      ],
      cta: { id: 'lumio-budget-limits', icon: 'edit' },
    },
    selfManagedChannels: SELF_MANAGED,
    campaigns: {
      tabs: [
        { id: 'suggestions', count: suggestions.length, badgeColor: 'magenta' },
        { id: 'running', count: countOf('running'), badgeColor: 'green' },
        { id: 'paused', count: countOf('paused'), badgeColor: 'muted' },
        { id: 'ended', count: countOf('ended'), badgeColor: 'muted' },
      ],
      filters: FILTERS,
      pool,
    },
    performanceOverview: PERFORMANCE_OVERVIEW,
  };
}

/**
 * The acquisition gauge score + band label for a brand `variant`, WITHOUT building
 * the whole page dataset. The Home dashboard's "Acquisition" optimization-score
 * gauge uses this so it shows the exact same number (and label) the Acquisition
 * page computes for that brand — one source of truth for the score. Pass the same
 * `budget` override AND `suggestedCount` (the live count of suggested campaigns) the
 * Acquisition page uses so both pages read the real wallet / daily cap / queue and
 * never disagree. Omit `suggestedCount` and the mock profile queue stands.
 */
export const acquisitionScoreForVariant = (
  variant: AcqBrandVariant = 0,
  budget?: BudgetOverride,
  suggestedCount?: number,
): { score: number; status: string } => {
  const profile = ACQ_BRAND_VARIANTS[variant] ?? ACQ_BRAND_VARIANTS[0];
  const { score } = computeAcquisitionScore({
    // The real live queue when provided; otherwise the mock slice the page renders
    // (≤ SUGGESTIONS.length).
    queuedSuggestions: suggestedCount ?? Math.min(profile.suggestions, SUGGESTIONS.length),
    availableBudget: budget?.availableBudget ?? profile.availableBudget,
    dailySpendLimit: budget?.dailySpendLimit ?? profile.dailySpendLimit,
  });
  return { score, status: acquisitionScoreSummary(score).status };
};

// Canonical dataset (variant 0). The campaign detail + operations-promotion-detail
// pages import this and resolve campaigns against the FULL pool, so a deep-link
// works regardless of which brand slice was last shown on the Acquisition page.
const CANONICAL = buildAcquisitionData(0);
export const acquisitionData: AcquisitionData = {
  ...CANONICAL,
  campaigns: { ...CANONICAL.campaigns, pool: FULL_POOL },
};

export default acquisitionData;
